import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import * as M from 'moment/moment';
import { extendMoment } from 'moment-range';
import { IntersectionId, SessionId } from '../../types/primitives';
import Intersection from '../../types/Intersection';
import { AggDataElement } from '../../types/AggDataElement';
import { ChartHeightMode } from '../../types/ChartHeightMode';
import { DayOfWeek } from '../../types/DayOfWeek';

const moment = extendMoment(M);
const dateFormat = 'YYYY-MM-DD HH:mm:ss';

interface PmSliceState {
  startDate: string;
  endDate: string;
  availableIntersections: Intersection[];
  intersectionTrajCountDict: { [key: string]: number } | null;
  selectedIntersections: IntersectionId[];
  selectedDaysOfWeek: DayOfWeek[];
  submitted: boolean;
  currentMouseOverIntersection: IntersectionId | null;
  selectedIntersectionOnChart: { intersectionId: IntersectionId, chartId: string } | null;
  threshold: number;
  exportData: AggDataElement[];
  phases: { [key: number]: string };
  journeyIdArray: string[];
  userEmail: string | null;
  role: string | null;
  sessionId: SessionId | null;
  organizationName: string | null;
  mapCenter: string;
  mapZoom: number;
  clusterMarkers: boolean;
  chartHeightMode: ChartHeightMode;
  customChartHeight: number;
  customYAxisGap: number;
  timezoneMap: { [key: IntersectionId]: string };
  isDataLoading: boolean;
  labelFontSize: number;
}

const initialState: PmSliceState = {
  startDate: moment().subtract(1, 'month').format(dateFormat),
  endDate: moment().subtract(1, 'day').endOf('day').format(dateFormat),
  // startDate: moment('2021-10-01')
  //   .format(dateFormat),
  // endDate: moment('2021-10-31')
  //   .endOf('day')
  //   .format(dateFormat),
  availableIntersections: [],
  intersectionTrajCountDict: null,
  // selectedIntersections: IS_DEV_VERSION
  //   ? [3144, 3143, 3142].map((i: number) => i as IntersectionId) : [],
  selectedIntersections: [],
  selectedDaysOfWeek: [],
  submitted: false,
  currentMouseOverIntersection: null,
  selectedIntersectionOnChart: null,
  threshold: 10,
  exportData: [],
  phases: {
    1: 'sb-mainline-left',
    2: 'nb-mainline-through',
    3: 'wb-side-left',
    4: 'eb-side-through',
    5: 'nb-mainline-left',
    6: 'sb-mainline-through',
    7: 'eb-side-left',
    8: 'wb-side-through',
  },
  journeyIdArray: [],
  userEmail: null,
  role: null,
  sessionId: null,
  organizationName: null,
  mapCenter: 'POINT(-86.156207 39.801804)',
  mapZoom: 8,
  clusterMarkers: localStorage.getItem('clusterMarkers') ? localStorage.getItem('clusterMarkers') === 'true' : true,
  chartHeightMode: localStorage.getItem('chartHeightMode') as ChartHeightMode || 'dynamic',
  customChartHeight: parseInt(localStorage.getItem('customChartHeight') || '300'),
  customYAxisGap: parseInt(localStorage.getItem('customYAxisGap') || '250'),
  timezoneMap: {},
  isDataLoading: false,
  labelFontSize: parseInt(localStorage.getItem('labelFontSize') || '12'),
};

const slice = createSlice({
  name: 'pm',
  initialState,
  reducers: {
    setPmState: (pm: { [key: string]: unknown }, action) => {
      Object.keys(action.payload)
        .forEach((key) => {
          // eslint-disable-next-line no-param-reassign
          pm[key] = action.payload[key];
        });
    },
    selectIntersections: ((state, action: PayloadAction<IntersectionId[]>) => {
      // eslint-disable-next-line no-param-reassign
      state.selectedIntersections = [...action.payload, ...current(state).selectedIntersections];
    }),
    deselectIntersections: ((state, action: PayloadAction<IntersectionId[]>) => {
      // eslint-disable-next-line no-param-reassign
      state.selectedIntersections = current(state)
        .selectedIntersections
        .filter((item) => !action.payload.includes(item));
    }),
  },
});

export const {
  setPmState,
  selectIntersections,
  deselectIntersections,
} = slice.actions;
export default slice.reducer;
